import { mutations } from "@/graphql";
import { useMutation } from "@apollo/client/react";
import { useCallback, useContext } from "react";
import { enqueueSnackbar } from "notistack";
import { useTextDomainContext, useUser } from "@/app/providers";

interface UseReadAllNotificationsVariables {
  userId?: string;
  email?: string;
}

interface UseReadAllNotificationsReturn {
  readAllNotifications: (variables?: UseReadAllNotificationsVariables) => void;
  readingAll: boolean;
}

/**
 * Custom hook to read all notifications.
 * @param {Function} [onCompleted] - Callback function to be called when the mutation completes successfully.
 * @param {Function} [onError] - Callback function to be called when an error occurs during the mutation.
 * @param {UseReadAllNotificationsVariables} [defaultVariables] - Default variables to use if not passed on call.
 */
export const useReadAllNotifications = (
  onCompleted: (_data: any) => void = () => {},
  onError: (_err: any) => void = () => {},
  defaultVariables?: UseReadAllNotificationsVariables,
): UseReadAllNotificationsReturn => {
  const { TextDomainContext } = useTextDomainContext();
  const { gettext } = useContext(TextDomainContext);
  const { user } = useUser();

  const [readAllNotifications, { loading }] = useMutation<any>(
    mutations.readAllNotifications,
    {
      onCompleted: (_data) => {
        onCompleted(_data);

        if (
          (_data as any)?.readAllNotifications?.success ||
          (_data as any)?.comReadAllNotifications?.success
        ) {
          enqueueSnackbar(gettext("All notifications marked as read"), {
            variant: "success",
          });
        }
      },
      onError: (_err) => {
        onError(_err);

        enqueueSnackbar("Read All Notifications: " + _err.message, {
          variant: "error",
        });
      },
      refetchQueries: [
        "getAllNotifications",
        "getNewNotifications",
        "comGetAllNotifications",
        "comGetNewNotifications",
      ],
    },
  );

  const readAll = useCallback(
    (variables?: UseReadAllNotificationsVariables) => {
      const targetUserId = variables?.userId ?? defaultVariables?.userId;
      const targetEmail =
        variables?.email ??
        defaultVariables?.email ??
        (targetUserId ? undefined : user?.email);

      readAllNotifications({
        variables: {
          userId: targetUserId,
          email: targetEmail,
        },
      }).then();
    },
    [readAllNotifications, defaultVariables, user?.email],
  );

  return {
    readAllNotifications: readAll,
    readingAll: loading,
  };
};
